import _ from 'lodash';
import { WatchListFilters } from 'services/clients/watchList';
import { LegacyContainerStatus } from 'types/Watchlist';
import { ImportSatusValue } from '../../../../../services/clients/watchList';

export type MilestoneFilterKey =
  | 'availableForPickup'
  | 'demurrageDue'
  | 'closedArea'
  | 'pickupAppointmentStatus'
  | 'returnAppointmentStatus'
  | 'holdTypes'
  | 'lastFreeDayAt'
  | 'gatedOutAt'
  | 'gatedInAt'
  | 'pickupAppointmentAt'
  | 'shippingLine'
  | 'containerType'
  | 'terminalPickupKeys'
  | 'portPickupCodes'
  | 'customers'
  | 'watchedAt';

export type StatusFilterKey =
  | 'importStatus'
  | 'containerStatus'
  | 'shippingLine'
  | 'containerType'
  | 'terminalPickupKeys';

/** All keys that act as filters of any kind */
export type FilterKey = MilestoneFilterKey | StatusFilterKey;

const MILESTONE_FILTER_KEYS: MilestoneFilterKey[] = [
  'availableForPickup',
  'demurrageDue',
  'closedArea',
  'pickupAppointmentStatus',
  'returnAppointmentStatus',
  'holdTypes',
  'lastFreeDayAt',
  'gatedOutAt',
  'gatedInAt',
  'pickupAppointmentAt',
  'watchedAt',
  'shippingLine',
  'containerType',
  'terminalPickupKeys',
  'portPickupCodes',
  'customers',
];

const STATUS_FILTER_KEYS: StatusFilterKey[] = [
  'importStatus',
  'containerStatus',
  'shippingLine',
  'containerType',
  'terminalPickupKeys',
];

const FILTER_KEYS: FilterKey[] = [...MILESTONE_FILTER_KEYS, ...STATUS_FILTER_KEYS];

/**
 * Go over all the milestone filter keys that are stored in the URL
 * For each key compare if the value is falsy --> Empty Array, Object with only null values, null value
 */
const filtersOn = (searchParams: WatchListFilters, filters: FilterKey[]): boolean => {
  const activeFilters = filters.map((k) => {
    const v = searchParams[k as keyof WatchListFilters];
    if (
      _.isNil(v) ||
      (_.isArray(v) && (v as string[]).length === 0) ||
      (_.isObject(v) && _.values(v).every(_.isEmpty))
    ) {
      return false;
    }

    return true;
  });
  return activeFilters.filter(_.identity).length > 0;
};

export const milestoneFiltersOn = (searchParams: WatchListFilters): boolean => {
  return filtersOn(searchParams, MILESTONE_FILTER_KEYS);
};

export const anyFiltersOn = (searchParams: WatchListFilters): boolean => {
  return filtersOn(searchParams, FILTER_KEYS);
};

/**
 * Returns a params object that can be used to clear all the milestone filters
 */
export const clearFilters = () => {
  const params: Partial<Record<FilterKey, null>> = {};
  FILTER_KEYS.forEach((k) => {
    params[k] = null;
  });
  return params;
};

interface FiltersToBoolean {
  onVesselStatus: boolean;
  arrivedStatus: boolean;
  gatedOutStatus: boolean;
  terminatedStatus: boolean;
  notFoundStatus: boolean;
  availableForPickup: null | boolean;
  allHolds: boolean;
  customsHolds: boolean;
  freightHolds: boolean;
  lineHolds: boolean;
  tmfHolds: boolean;
  terminalHolds: boolean;
  ctfHolds: boolean;
  othersHolds: boolean;
  lastFreeDayToday: boolean;
  lastFreeDayTomorrow: boolean;
  gatedOutMoreThanFive: boolean;
  gatedOutLessThanFive: boolean;
  closedArea: null | boolean;
  demurrageDue: null | boolean;
  pickupAppointmentScheduledToday: boolean;
  pickupAppointmentScheduledTomorrow: boolean;
  pickupAppointmentScheduledAnyTime: boolean;
  gatedOutToday: boolean;
  gatedOutYesterday: boolean;
  terminatedToday: boolean;
  terminatedYesterday: boolean;
  shippingLine: string[];
  containerType: string[];
  terminalPickupKeys: string[];
  customers: string[];
}

/**
 * Usedd for analytics
 */
export const filtersToBoolean = (searchParams: WatchListFilters) => {
  const filters: FiltersToBoolean = {
    onVesselStatus: false,
    arrivedStatus: false,
    gatedOutStatus: false,
    terminatedStatus: false,
    notFoundStatus: false,
    availableForPickup: null,
    allHolds: false,
    customsHolds: false,
    freightHolds: false,
    lineHolds: false,
    tmfHolds: false,
    terminalHolds: false,
    ctfHolds: false,
    othersHolds: false,
    lastFreeDayToday: false,
    lastFreeDayTomorrow: false,
    gatedOutMoreThanFive: false,
    gatedOutLessThanFive: false,
    closedArea: null,
    demurrageDue: null,
    pickupAppointmentScheduledToday: false,
    pickupAppointmentScheduledTomorrow: false,
    pickupAppointmentScheduledAnyTime: false,
    gatedOutToday: false,
    gatedOutYesterday: false,
    terminatedToday: false,
    terminatedYesterday: false,
    shippingLine: [],
    containerType: [],
    terminalPickupKeys: [],
    customers: [],
  };
  if (searchParams.containerStatus && filtersOn(searchParams, ['containerStatus'])) {
    filters.notFoundStatus = searchParams.containerStatus.includes(LegacyContainerStatus.NotFound);
    filters.gatedOutStatus = searchParams.containerStatus.includes(LegacyContainerStatus.GatedOut);
    filters.terminatedStatus = searchParams.containerStatus.includes(
      LegacyContainerStatus.Terminated
    );
  }

  if (searchParams.importStatus && filtersOn(searchParams, ['importStatus'])) {
    filters.onVesselStatus = searchParams.importStatus.includes('vessel');
    filters.arrivedStatus = ['yard', 'rail', 'unknown'].every(
      (v: string) =>
        searchParams.importStatus && searchParams.importStatus.includes(v as ImportSatusValue)
    );
  }

  if (searchParams.availableForPickup && filtersOn(searchParams, ['availableForPickup'])) {
    filters.availableForPickup = searchParams.availableForPickup;
  }

  if (searchParams.holdTypes && filtersOn(searchParams, ['holdTypes'])) {
    filters.allHolds = searchParams.holdTypes.includes('all');
    filters.customsHolds = searchParams.holdTypes.includes('customs');
    filters.freightHolds = searchParams.holdTypes.includes('freight');
    filters.lineHolds = searchParams.holdTypes.includes('line');
    filters.tmfHolds = searchParams.holdTypes.includes('tmf');
    filters.terminalHolds = searchParams.holdTypes.includes('terminal');
    filters.ctfHolds = searchParams.holdTypes.includes('ctf');
    filters.othersHolds = searchParams.holdTypes.includes('others');
  }

  if (searchParams.lastFreeDayAt && filtersOn(searchParams, ['lastFreeDayAt'])) {
    filters.lastFreeDayToday =
      searchParams.lastFreeDayAt.exactDate === 'today' ||
      searchParams.lastFreeDayAt.startDate === 'today';
    filters.lastFreeDayTomorrow =
      searchParams.lastFreeDayAt.exactDate === 'tomorrow' ||
      searchParams.lastFreeDayAt.endDate === 'tomorrow';
  }

  if (searchParams.gatedOutAt && filtersOn(searchParams, ['gatedOutAt'])) {
    filters.gatedOutMoreThanFive = searchParams.gatedOutAt.endDate === '-5:days';
    filters.gatedOutLessThanFive = searchParams.gatedOutAt.startDate === '-5:days';
  }
  if (!_.isNil(searchParams.closedArea) && filtersOn(searchParams, ['closedArea'])) {
    filters.closedArea = searchParams.closedArea;
  }

  if (!_.isNil(searchParams.demurrageDue) && filtersOn(searchParams, ['demurrageDue'])) {
    filters.demurrageDue = searchParams.demurrageDue;
  }

  if (
    (searchParams.pickupAppointmentAt || searchParams.pickupAppointmentStatus) &&
    filtersOn(searchParams, ['pickupAppointmentStatus', 'pickupAppointmentAt'])
  ) {
    filters.pickupAppointmentScheduledToday =
      searchParams.pickupAppointmentAt?.startDate === 'today' ||
      searchParams.pickupAppointmentAt?.exactDate === 'today';

    filters.pickupAppointmentScheduledTomorrow =
      searchParams.pickupAppointmentAt?.endDate === 'tomorrow' ||
      searchParams.pickupAppointmentAt?.exactDate === 'tomorrow';

    if (
      !filters.pickupAppointmentScheduledTomorrow &&
      !filters.pickupAppointmentScheduledToday &&
      searchParams.pickupAppointmentStatus?.includes('scheduled')
    ) {
      filters.pickupAppointmentScheduledAnyTime = true;
    }
  }

  if (searchParams.gatedOutAt && filtersOn(searchParams, ['gatedOutAt'])) {
    filters.gatedOutToday =
      searchParams.gatedOutAt.exactDate === 'today' || searchParams.gatedOutAt.endDate === 'today';
    filters.gatedOutYesterday =
      searchParams.gatedOutAt.exactDate === '-1:days' ||
      searchParams.gatedOutAt.startDate === '-1:days';
  }

  if (searchParams.gatedInAt && filtersOn(searchParams, ['gatedInAt'])) {
    filters.terminatedToday =
      searchParams.gatedInAt.exactDate === 'today' || searchParams.gatedInAt.endDate === 'today';
    filters.terminatedYesterday =
      searchParams.gatedInAt.exactDate === '-1:days' ||
      searchParams.gatedInAt.startDate === '-1:days';
  }

  if (searchParams.shippingLine && filtersOn(searchParams, ['shippingLine'])) {
    filters.shippingLine = searchParams.shippingLine;
  }

  if (searchParams.containerType && filtersOn(searchParams, ['containerType'])) {
    filters.containerType = searchParams.containerType;
  }

  if (searchParams.terminalPickupKeys && filtersOn(searchParams, ['terminalPickupKeys'])) {
    filters.terminalPickupKeys = searchParams.terminalPickupKeys;
  }

  if (searchParams.customers && filtersOn(searchParams, ['customers'])) {
    filters.customers = searchParams.customers;
  }

  return filters;
};
